import { animate, useMotionValue, type AnimationPlaybackControls, type MotionValue } from 'framer-motion';
import { type PointerEvent, useCallback, useEffect, useRef, useState } from 'react';
import { shortestStep, wrapIndex } from './orbit-math';

const DRAG_SENSITIVITY = 0.012;
const SPRING_STIFFNESS = 280;
const SPRING_DAMPING = 32;
const DRAG_THRESHOLD_PX = 8;

interface DragState {
    pointerId: number;
    startX: number;
    startPosition: number;
    lastX: number;
    lastTime: number;
    velocity: number;
    moved: boolean;
}

interface Options {
    itemCount: number;
    initialIndex: number;
    /** Continuous drift in items per second (0 = off). Positive turns the ring clockwise seen from above. */
    driftSpeed: number;
    /** Hover etc.: stop drifting and settle on the nearest item. */
    isPaused: boolean;
    onSettled?: (index: number) => void;
}

export interface OrbitPosition {
    position: MotionValue<number>;
    activeIndex: number;
    /** True while the ring is drifting on its own. */
    isDrifting: boolean;
    goToIndex: (index: number, force?: boolean) => void;
    stepBy: (delta: number) => void;
    /** True right after a drag gesture, so the click that ends it does not select a tile. */
    consumeDragClick: () => boolean;
    onPointerDown: (event: PointerEvent<HTMLElement>) => void;
    onPointerMove: (event: PointerEvent<HTMLElement>) => void;
    onPointerUp: (event: PointerEvent<HTMLElement>) => void;
}

export function useOrbitPosition({
    itemCount,
    initialIndex,
    driftSpeed,
    isPaused,
    onSettled,
}: Options): OrbitPosition {
    const position = useMotionValue(initialIndex);
    const [activeIndex, setActiveIndex] = useState(() => wrapIndex(initialIndex, itemCount));
    const drag = useRef<DragState | null>(null);
    const animation = useRef<AnimationPlaybackControls | null>(null);
    const isDragging = useRef(false);
    const suppressClick = useRef(false);
    const settledCallback = useRef(onSettled);
    const [reduceMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const isDrifting = driftSpeed !== 0 && !isPaused && !reduceMotion && itemCount > 1;

    useEffect(() => {
        settledCallback.current = onSettled;
    }, [onSettled]);

    const stopAnimation = useCallback(() => {
        animation.current?.stop();
        animation.current = null;
    }, []);

    const springTo = useCallback(
        (target: number, velocity = 0) => {
            if (itemCount <= 0) {
                return;
            }

            stopAnimation();
            const destination = Math.round(target);

            animation.current = animate(position, destination, {
                type: 'spring',
                stiffness: SPRING_STIFFNESS,
                damping: SPRING_DAMPING,
                velocity,
                onUpdate: (latest) => setActiveIndex(wrapIndex(Math.round(latest), itemCount)),
                onComplete: () => {
                    const settled = wrapIndex(destination, itemCount);

                    // Keep the raw value bounded after very long spins.
                    if (Math.abs(destination) > itemCount * 20) {
                        position.set(settled);
                    }

                    setActiveIndex(settled);
                    settledCallback.current?.(settled);
                },
            });
        },
        [itemCount, position, stopAnimation],
    );

    const goToIndex = useCallback(
        (index: number, force = false) => {
            if (itemCount <= 0) {
                return;
            }

            if (!force && suppressClick.current) {
                suppressClick.current = false;
                return;
            }

            const current = Math.round(position.get());
            springTo(current + shortestStep(wrapIndex(current, itemCount), index, itemCount));
        },
        [itemCount, position, springTo],
    );

    const stepBy = useCallback(
        (delta: number) => {
            if (itemCount > 0) {
                springTo(Math.round(position.get()) + delta);
            }
        },
        [itemCount, position, springTo],
    );

    const consumeDragClick = useCallback(() => {
        if (!suppressClick.current) {
            return false;
        }

        suppressClick.current = false;

        return true;
    }, []);

    const onPointerDown = useCallback(
        (event: PointerEvent<HTMLElement>) => {
            if (itemCount <= 0 || event.button !== 0) {
                return;
            }

            stopAnimation();
            isDragging.current = true;
            suppressClick.current = false;
            const now = performance.now();

            drag.current = {
                pointerId: event.pointerId,
                startX: event.clientX,
                startPosition: position.get(),
                lastX: event.clientX,
                lastTime: now,
                velocity: 0,
                moved: false,
            };
        },
        [itemCount, position, stopAnimation],
    );

    const onPointerMove = useCallback(
        (event: PointerEvent<HTMLElement>) => {
            const state = drag.current;

            if (!state || state.pointerId !== event.pointerId) {
                return;
            }

            const now = performance.now();
            const elapsed = Math.max(now - state.lastTime, 1);
            state.velocity = (event.clientX - state.lastX) / elapsed;
            state.lastX = event.clientX;
            state.lastTime = now;

            const distance = event.clientX - state.startX;

            if (!state.moved && Math.abs(distance) > DRAG_THRESHOLD_PX) {
                state.moved = true;
                // Capture only once this is a real drag so plain clicks keep reaching tiles.
                event.currentTarget.setPointerCapture(event.pointerId);
            }

            if (state.moved) {
                position.set(state.startPosition - distance * DRAG_SENSITIVITY);
                setActiveIndex(wrapIndex(Math.round(position.get()), itemCount));
            }
        },
        [itemCount, position],
    );

    const onPointerUp = useCallback(
        (event: PointerEvent<HTMLElement>) => {
            const state = drag.current;

            if (!state || state.pointerId !== event.pointerId) {
                return;
            }

            isDragging.current = false;
            drag.current = null;

            try {
                event.currentTarget.releasePointerCapture(event.pointerId);
            } catch {
                // Capture was never taken for a plain click.
            }

            if (state.moved) {
                suppressClick.current = true;
                springTo(position.get() - state.velocity * 8, -state.velocity * 40);
            }
        },
        [position, springTo],
    );

    // Continuous drift; yields to drags and to spring animations (keyboard, settle).
    useEffect(() => {
        if (!isDrifting) {
            return;
        }

        let frame = 0;
        let last = performance.now();
        const tick = (now: number) => {
            const dt = Math.min(0.1, (now - last) / 1000);
            last = now;

            if (!isDragging.current && !animation.current) {
                let next = position.get() + driftSpeed * dt;

                if (Math.abs(next) > itemCount * 20) {
                    next = wrapIndex(next, itemCount);
                }

                position.set(next);
                const index = wrapIndex(Math.round(next), itemCount);
                setActiveIndex((current) => (current === index ? current : index));
            }

            frame = window.requestAnimationFrame(tick);
        };
        frame = window.requestAnimationFrame(tick);

        return () => window.cancelAnimationFrame(frame);
    }, [driftSpeed, isDrifting, itemCount, position]);

    // When drifting stops (hover), come to rest on the nearest item.
    useEffect(() => {
        if (!isDrifting && driftSpeed !== 0 && !isDragging.current && !animation.current) {
            const current = position.get();

            if (Math.abs(current - Math.round(current)) > 0.001) {
                springTo(current);
            }
        }
    }, [driftSpeed, isDrifting, position, springTo]);

    useEffect(() => {
        if (itemCount <= 0) {
            return;
        }

        const settled = wrapIndex(Math.round(position.get()), itemCount);
        setActiveIndex(settled);
        position.set(settled);
    }, [itemCount]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => stopAnimation, [stopAnimation]);

    return {
        position,
        activeIndex,
        isDrifting,
        goToIndex,
        stepBy,
        consumeDragClick,
        onPointerDown,
        onPointerMove,
        onPointerUp,
    };
}
